import { createEntityId } from '@/core/EntityId';

export type PruningStyle = 'natural' | 'formal' | 'columnar' | 'rounded' | 'hedged';

export const PRUNING_STYLES: readonly PruningStyle[] = [
  'natural',
  'formal',
  'columnar',
  'rounded',
  'hedged',
];

export interface Vec3 {
  x: number;
  y: number;
  z: number;
}

/**
 * Explicit decisions about one planted specimen that outrank both the catalog and the site profile.
 *
 * The last tier of the resolution chain: master flora → regional/climate → site conditions →
 * **instance overrides**. Every field is optional and the whole block is absent until something is
 * actually overridden, because "nobody has said" and "somebody said the default" are different facts
 * and only the second one should survive a round trip through the project file.
 *
 * Note what is *not* here: a resolved size, a resolved bloom date, a suitability rating. Those are
 * derived from this block by `localization/FloraResolver.ts` every time they are asked for. Writing a
 * derived value onto the instance would be the same mistake as copying a flora record per zone —
 * it would go stale the moment the site was re-localized.
 *
 * The per-plant *phenology* adjustment is the existing {@link FloraInstance.seasonalOffsetDays},
 * deliberately not duplicated here: one field that shifts a plant's calendar is enough, and two would
 * eventually disagree.
 */
export interface PlantSiteOverrides {
  /** This specimen is watered, whatever the region does. Softens a moisture warning. */
  irrigated?: boolean;
  /** A south wall, a courtyard, a cold frame — worth about half a zone of extra hardiness. */
  protectedMicroclimate?: boolean;
  /** In a pot: roots are exposed to air temperature, which costs roughly a full zone. */
  containerPlanted?: boolean;
  /** Kept to this height by pruning, overriding whatever the growth model would produce. */
  managedHeightMeters?: number;
  /** Kept to this spread. Hedges and espaliers are the ordinary case. */
  managedSpreadMeters?: number;
  /** Multiplier on the site-resolved mature size, for a specimen known to run large or small. */
  growthScale?: number;
}

/** Whether an override block holds any actual decision. Used to keep empty blocks out of the file. */
export function hasSiteOverrides(overrides: PlantSiteOverrides | undefined): boolean {
  if (!overrides) return false;
  return Object.values(overrides).some((value) => value !== undefined);
}

/**
 * A placed plant — a separate project entity (spec §10). Plain serializable data, independent from
 * its {@link FloraDefinition}. Modifiers affect the procedural model without mutating the
 * definition.
 */
export interface FloraInstance {
  id: string;
  floraDefinitionId: string;
  position: Vec3;
  rotation: Vec3;
  /** 0 = smallest nursery size, 1 = typical mature. May exceed 1 for exceptional specimens. */
  maturity: number;
  containerStage?: string;
  visualSeed: number;
  health: number;
  fullness: number;
  seasonalOffsetDays: number;
  /**
   * When set, this plant ignores the global season and is rendered at this fixed day of year,
   * isolating it from date changes (spec §5.4). The per-plant {@link seasonalOffsetDays} still
   * applies to the global season when this override is absent.
   */
  seasonOverrideDayOfYear?: number;
  sizeVariation: number;
  colorVariation: number;
  pruningStyle: PruningStyle;
  aliasGroupId?: string;
  groupId?: string;
  locked: boolean;
  hidden: boolean;
  /** Present only when something has actually been overridden. See {@link PlantSiteOverrides}. */
  siteOverrides?: PlantSiteOverrides;
}

/** Maximum maturity when the "exceptional specimen" over-growth flag is enabled. */
export const MAX_OVERGROWN_MATURITY = 1.5;

/**
 * Clamp maturity into range. By default [0, 1]; when `allowOvergrowth` is set, permits values up to
 * {@link MAX_OVERGROWN_MATURITY} for exceptional specimens (spec §10).
 */
export function clampMaturity(value: number, allowOvergrowth = false): number {
  if (!Number.isFinite(value)) return 0;
  const upper = allowOvergrowth ? MAX_OVERGROWN_MATURITY : 1;
  return Math.min(Math.max(value, 0), upper);
}

export interface CreateFloraInstanceOptions {
  position?: Vec3;
  rotation?: Vec3;
  maturity?: number;
  visualSeed?: number;
  allowOvergrowth?: boolean;
  overrides?: Partial<FloraInstance>;
}

/**
 * Create a new flora instance with sensible default modifiers. Maturity is clamped on creation.
 * `visualSeed` defaults to a deterministic-friendly random integer (callers may pass a fixed seed
 * for reproducibility).
 */
export function createFloraInstance(
  floraDefinitionId: string,
  options: CreateFloraInstanceOptions = {},
): FloraInstance {
  const maturity = clampMaturity(options.maturity ?? 0.5, options.allowOvergrowth ?? false);
  const instance: FloraInstance = {
    id: createEntityId('flora-'),
    floraDefinitionId,
    position: options.position ?? { x: 0, y: 0, z: 0 },
    rotation: options.rotation ?? { x: 0, y: 0, z: 0 },
    maturity,
    visualSeed: options.visualSeed ?? Math.floor(Math.random() * 0x7fffffff),
    health: 1,
    fullness: 1,
    seasonalOffsetDays: 0,
    sizeVariation: 0,
    colorVariation: 0,
    pruningStyle: 'natural',
    locked: false,
    hidden: false,
    ...options.overrides,
  };
  return instance;
}

/** Deep clone an instance as a fully independent entity with a fresh id (used by Duplicate). */
export function cloneFloraInstanceIndependent(source: FloraInstance): FloraInstance {
  const clone: FloraInstance = {
    ...source,
    id: createEntityId('flora-'),
    position: { ...source.position },
    rotation: { ...source.rotation },
  };
  // A duplicate is independent: it must not remain in the source's alias group.
  delete clone.aliasGroupId;
  return clone;
}
